import { useMutation, useQueryClient } from "@tanstack/react-query";
import Big from "big.js";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { parseEther } from "viem";
import { usePublicClient, useWriteContract } from "wagmi";
import { GATEWAY_ABI } from "@/contract/abi/gateway";
import ContractAddress from "@/contract/addresses";
import { useContractErrorHandler } from "@/hooks/useContractErrorHandler";
import { $api } from "@/lib/api-client";
import type { WithdrawMode } from "@/types/api/user.types";

const WITHDRAW_FUNCTION_NAMES = {
  DNM: "userWithdrawDNM",
  VOUCHER: "userWithdrawDNMVoucher",
  SELLER: "sellerWithdrawDNM",
  UVM: "userWithdrawWeekUVM",
} as const satisfies Record<WithdrawMode, string>;

/** Seller withdrawals record their existing DNM history in useWithdrawSellerDNM. */
const HISTORY_TYPES = {
  DNM: "PURCHASE",
  UVM: "UVM",
  VOUCHER: "VOUCHER",
} as const satisfies Partial<Record<WithdrawMode, string>>;

interface UseWithdrawPendingOptions {
  /**
   * Refresh the on-chain figures this withdrawal just changed. Awaited INSIDE
   * the mutation, deliberately: `isPending` is what disables and spins the
   * button, so anything awaited out here (in `onSuccess`, or by the caller
   * after `mutateAsync`) leaves a window where the button is live again while
   * the screen still shows the pre-withdrawal balance. Callers pass their
   * `useFetchContractUser().refetch`.
   */
  onConfirmed?: () => Promise<unknown> | unknown;
}

export const useWithdrawPending = (
  mode: WithdrawMode,
  options?: UseWithdrawPendingOptions,
) => {
  const t = useTranslations("stake");
  const { handleContractError } = useContractErrorHandler();
  const queryClient = useQueryClient();
  const publicClient = usePublicClient();

  const { writeContractAsync: withdraw } = useWriteContract();
  // Undefined for modes that keep no history. Hoisted so the write below and
  // the cache invalidation in onSuccess cannot drift apart -- an invalidation
  // aimed at a key the reader no longer uses fails silently and leaves the
  // modal showing a stale list.
  const historyType:
    | (typeof HISTORY_TYPES)[keyof typeof HISTORY_TYPES]
    | undefined = HISTORY_TYPES[mode as keyof typeof HISTORY_TYPES];
  const createHistoryMutation = $api.useMutation(
    "post",
    "/dnm-reward-withdraw",
  );

  async function mutationFn(amount: number) {
    // Not `publicClient?.` -- optional-chaining the receipt wait means a
    // missing client silently skips it, the mutation resolves the instant the
    // tx is SUBMITTED, and the button goes live (and the success toast fires)
    // while the withdrawal is still unmined. Fail loudly instead.
    if (!publicClient) {
      throw new Error("Public client unavailable");
    }

    const tx = await withdraw({
      abi: GATEWAY_ABI,
      address: ContractAddress.GATEWAY,
      functionName: WITHDRAW_FUNCTION_NAMES[mode],
      args: [parseEther(amount.toString())],
    });

    if (!tx) {
      throw new Error("Transaction hash not received");
    }

    // Receipt only -- no multi-node confirmation depth. One block is the
    // point at which the balance has actually changed, which is all the UI
    // is waiting to reflect.
    const receipt = await publicClient.waitForTransactionReceipt({ hash: tx });

    // A reverted tx still produces a receipt. Without this check the mutation
    // resolves successfully and the user is told the withdrawal worked when
    // the chain rejected it.
    if (receipt.status !== "success") {
      throw new Error("Transaction reverted");
    }

    if (historyType) {
      // Record right after on-chain confirmation, same pattern as
      // useWithdrawSellerDNM.ts. If this API call fails, the on-chain
      // withdrawal already succeeded -- don't fail the whole mutation over
      // a history-logging error, just log it and let the toast still read
      // as a success (which it is, on-chain).
      try {
        await createHistoryMutation.mutateAsync({
          body: {
            amount: Big(amount).toFixed(8),
            status: "SUCCESS",
            type: historyType,
          },
        });
      } catch (apiError) {
        console.error(
          `Failed to record ${historyType} withdrawal history:`,
          apiError,
        );
      }
    }

    // Still inside the mutation, so `isPending` -- and therefore the button's
    // disabled + loading state -- covers this too. The button only comes back
    // once the figures beside it are the post-withdrawal ones.
    await options?.onConfirmed?.();

    return tx;
  }

  return useMutation({
    mutationKey: ["withdraw-pending", mode],
    mutationFn,
    onError: (e) => handleContractError(e),
    onSuccess: () => {
      toast.success(t("toasts.withdrawSuccess"));
      queryClient.invalidateQueries({ queryKey: ["me"] });
      queryClient.invalidateQueries({ queryKey: ["seller-bv"] });
      if (historyType) {
        // Prefix match on ["withdraw-history", type] -- limit is the last key
        // segment and varies by caller, so matching the prefix catches every
        // page size rather than only the one this component happens to use.
        queryClient.invalidateQueries({
          queryKey: ["withdraw-history", historyType],
        });
      }
      // The pending/DNM/voucher balances are NOT refreshed from here.
      // useFetchContractUser reads through wagmi's useReadContract, which owns
      // an internal query key -- there is no literal key to invalidate, which
      // is why an older ["contract-user"] invalidation sat here doing nothing
      // and left the balance stale until a full page reload. The refresh is
      // the caller's `onConfirmed` instead, awaited inside the mutation so it
      // completes before the button becomes usable again.
    },
  });
};
